import type { InvoiceLineReport } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Panel, PanelHeader, formatCents } from "./ui";

/**
 * Settings → Sync: invoices from JobNimbus whose lines or total aren't QuickBooks's. Read only; each
 * one changes only when "Use QuickBooks's" is clicked for it.
 */
export function InvoiceLinesPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["invoice-lines"], queryFn: () => get<InvoiceLineReport>("/api/integrations/quickbooks/invoice-lines") });
  const [open, setOpen] = useState<string | null>(null);
  const [showWording, setShowWording] = useState(false);
  const follow = useMutation({ mutationFn: (id: string) => post(`/api/invoices/${id}/follow-quickbooks`), onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice-lines"] }) });
  const r = q.data;
  if (!r || r.checked === 0) return null;
  const list = r.differences.filter((d) => showWording || d.comparison === "amounts");
  return (
    <Panel>
      <PanelHeader
        title="Invoice lines vs QuickBooks"
        description="Invoices from JobNimbus, compared line by line with QuickBooks as of the last Sync. Nothing changes here unless you click Use QuickBooks's on one; that invoice then follows QuickBooks from then on."
      />
      <div className="flex flex-col gap-3 px-5 py-4 text-sm">
        {follow.error && <Alert>{errorMessage(follow.error)}</Alert>}
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span>{r.checked.toLocaleString("en-US")} checked</span>
          <span className="text-ok">{r.same.toLocaleString("en-US")} match</span>
          <span className="text-wait">{r.amounts.toLocaleString("en-US")} with different amounts</span>
          <button className="text-muted underline-offset-2 hover:underline" onClick={() => setShowWording(!showWording)}>
            {r.wording.toLocaleString("en-US")} worded differently (same amounts){showWording ? ": hide" : ": show"}
          </button>
          {r.following > 0 && <span className="text-muted">{r.following.toLocaleString("en-US")} follow QuickBooks</span>}
        </div>
        {list.length > 0 && (
          <ul className="max-h-[32rem] overflow-auto rounded-[var(--radius-control)] border border-line">
            {list.map((d) => (
              <li key={d.invoiceId} className="border-b border-mist px-3 py-2 last:border-0">
                <div className="flex flex-wrap items-center gap-2">
                  <a href={`/jobs/${d.jobId}?tab=invoices`} className="font-semibold hover:text-brand">
                    {d.jobLabel}
                  </a>
                  <span>Invoice {d.number}</span>
                  <Badge tone={d.comparison === "amounts" ? "wait" : "off"}>{d.comparison === "amounts" ? "Different amounts" : "Worded differently"}</Badge>
                  <span className="text-muted">
                    here {formatCents(d.totalCents)} · QuickBooks {formatCents(d.qbTotalCents)} · {d.lines.length} vs {d.qbLines.length} lines
                  </span>
                  <span className="flex-1" />
                  <button className="text-muted hover:text-ink" onClick={() => setOpen(open === d.invoiceId ? null : d.invoiceId)}>
                    {open === d.invoiceId ? "Hide lines" : "Compare lines"}
                  </button>
                  <Button className="px-3 py-1" busy={follow.isPending && follow.variables === d.invoiceId} onClick={() => window.confirm(`Replace this invoice's lines and total with QuickBooks's (${formatCents(d.qbTotalCents)}, ${d.qbLines.length} lines)? It follows QuickBooks from then on.`) && follow.mutate(d.invoiceId)}>
                    Use QuickBooks&apos;s
                  </Button>
                </div>
                {open === d.invoiceId && (
                  <div className="mt-2 grid gap-3 sm:grid-cols-2">
                    {([["Here (from JobNimbus)", d.lines, d.totalCents], ["QuickBooks", d.qbLines, d.qbTotalCents]] as const).map(([title, lines, total]) => (
                      <div key={title} className="rounded-[var(--radius-control)] bg-mist/60 p-2">
                        <div className="mb-1 font-semibold">{title}</div>
                        {lines.length === 0 ? (
                          <div className="text-muted">No lines</div>
                        ) : (
                          <table className="w-full">
                            <tbody>
                              {lines.map((l, i) => (
                                <tr key={i} className="align-top">
                                  <td className="py-0.5 pr-2">{l.description}</td>
                                  <td className="py-0.5 text-right whitespace-nowrap">{formatCents(l.amountCents)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        <div className="mt-1 flex justify-between border-t border-line pt-1 font-semibold">
                          <span>Total</span>
                          <span>{formatCents(total)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}
